import { inject, Injectable, signal } from "@angular/core";
import { CustomFormControl, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ItemDefault, ItemFields, ItemsForm } from "src/app/features/items/models/items-form.model";
import { AllergenWithCheck, ItemDetailsSectionFormGroups, ItemDetailsSectionValidators } from "./item-details-section.model";
import { Validators } from "@angular/forms";
import { Allergens, Items } from "tilby-models";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { ItemDetailOptionInterface } from "src/app/features/items/interfaces/item-details.interfaces";
import { ConfigurationManagerService } from "src/app/core";

@Injectable()
export class ItemDetailsSectionFormService {

    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly configurationManager= inject(ConfigurationManagerService);
    
    private itemDetailsForm!: ItemDetailsSectionFormGroups;

    get form() {
        return this.itemDetailsForm;
    }
        
    itemDetailsSectionFormReady = signal(false);

    allergensList!: Array<Allergens&{checked?:boolean}> | undefined;
    optionNames: Array<ItemDetailOptionInterface> = [];

    validators: ItemDetailsSectionValidators = {
        orderNameMaxLength: 20,
        descriptionMaxLength: 255,
        optionMaxLength: 255,

        // --------
        provMinLength: 0,
        provMaxLength: 255,
        provMaxLengthArray: new Array(10).fill(2)
    };

    createForm(item: ItemFields = new ItemDefault() ,hiddenFields?:Array<keyof ItemFields>) {
        this.itemDetailsForm = new CustomFormGroup<ItemsForm>({
            order_name: new CustomFormControl(
                item.order_name || null, 
                { validators: [Validators.maxLength(this.validators.orderNameMaxLength)] }, 
            ),
            description: new CustomFormControl(
                item.description || null, 
                { validators: [Validators.maxLength(this.validators.descriptionMaxLength)] }, 
            ),
            alternative_descriptions: new CustomFormControl(
                (item.alternative_descriptions || []).map(alt => ({...alt}))
            ),
            is_frozen: new CustomFormControl(
                item.is_frozen
            ),
            checkout_mark: new CustomFormControl(
                item.checkout_mark
            ),
            allergens: new CustomFormControl(
                this.prepareAllergens(item) || []
            ),
            components: new CustomFormControl(
                [...item.components || []]
            ),
            images: new CustomFormControl(
                (item.images || []).map(img => {
                    return {
                        ...img,
                        uploaded: true
                    };
                })
            ),
            thumbnail: new CustomFormControl(
                item.thumbnail || null
            ),
            icon: new CustomFormControl(
                item.icon || null
            )
        });

        this.addOptionNames(item);

        this.itemDetailsSectionFormReady.set(true);
        return this.itemDetailsForm;
    }

    resetFormValues(item: Items = new ItemDefault()) {
        this.itemDetailsForm.patchValue({
            order_name: item.order_name || null,
            description: item.description || null,
            alternative_descriptions: (item.alternative_descriptions || []).map(alt => ({...alt})),
            is_frozen: item.is_frozen,
            checkout_mark: item.checkout_mark,
            allergens: this.prepareAllergens(item) || [],
            components: [...item.components || []],
            images: [...(item.images || []).map(img => {
                return {
                    ...img,
                    uploaded: true
                };
            })],
            thumbnail: item.thumbnail || null,
            icon: item.icon || null
        });
        this.resetOptionsValues(item);
    }

    private resetOptionsValues(item: Items) {
        this.optionNames.forEach(option => {
            let control = this.itemDetailsForm.get(option.targetField);
            control?.setValue(this.readDynamicProperty(item, option.targetField), {emitEvent: false});
        })
    }

    addOptionNames(item: Items) {
        if(!this.optionNames || this.optionNames.length == 0) {
            this.buildOptionNames();
        }
        this.optionNames.forEach(option => {
            this.itemDetailsForm.addControl(
                option.targetField, 
                new CustomFormControl(
                    this.readDynamicProperty(item, option.targetField) || null,
                    { validators: [Validators.maxLength(this.validators.optionMaxLength)] },
                )
            )
        })
    }

    private readDynamicProperty(item: Items, dynamicKey: string) {
        let value;
        if (Object.prototype.hasOwnProperty.call(item, dynamicKey)) {
            value = item[dynamicKey as keyof Items];
        } 
        return value;
    }

    buildOptionNames() {
        for(let idx = 1; idx <= 4; idx++) {
            let optionName = this.configurationManager.getPreference(`items.option${idx}_name`);
            if(optionName) {
                this.optionNames.push({
                    index: idx,
                    targetField: `option${idx}_value`,
                    fieldName: optionName
                });
            }
        }
    }

    resetOptionNames() {
        this.optionNames = [];
    }


    getDetailsSectionFields(currentItem: Items) {
        // Clono l'oggetto di destinazione per non mutare l'originale
        const updatedItem = { ...currentItem }; 

        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const itemDetailsFieldsToMap = Object.keys(this.itemDetailsForm.controls);

        // Ciclo su tutte le chiavi trovate nel form
        itemDetailsFieldsToMap.forEach(key => {
            const control = this.itemDetailsForm.get(key);

            if(control) {
                let value = control.value;

                 switch (key) {
                    case 'allergens':
                        value = (value || [])
                            .filter((allergen: AllergenWithCheck) => allergen.checked)
                            .map(({ checked, ...rest }: AllergenWithCheck) => rest);
                        break;

                    case 'images':
                        if (Array.isArray(value)) {
                            value = value.map(img => ({image_url: img.image_url}));
                        }
                        break;

                    default:
                        if (typeof value === 'string') {
                            value = value.trim() || null;
                        }
                        break;
                }
                
                // Assegno il valore all'oggetto.
                (updatedItem as any)[key] = value;
            }
        })
        return updatedItem;
    }

    get order_name() {
        return this.itemDetailsForm?.controls.order_name;
    }

    get description() {
        return this.itemDetailsForm?.controls.description;
    }

    get alternativeDescriptions() {
        return this.itemDetailsForm?.controls.alternative_descriptions;
    }

    get is_frozen() {
        return this.itemDetailsForm?.controls.is_frozen;
    }

    get checkout_mark() {
        return this.itemDetailsForm?.controls.checkout_mark;
    }

    get allergens() {
        return this.itemDetailsForm?.controls.allergens;
    }

    get components() {
        return this.itemDetailsForm?.controls.components;
    }

    get images() {
        return this.itemDetailsForm?.controls.images;
    }

    get thumbnail() {
        return this.itemDetailsForm?.controls.thumbnail;
    }

    get icon() {
        return this.itemDetailsForm?.controls.icon;
    }

    getOptionName(targetField: string)  {
        return this.itemDetailsForm.get(targetField);
    }

    getSpecificFormControl<K extends keyof typeof this.itemDetailsForm.controls>(controlName: K): CustomFormControl {
        return this.itemDetailsForm.controls[controlName] as CustomFormControl;
    }

    prepareAllergens(item:ItemFields) {
        let allergensList: Array<AllergenWithCheck> | undefined = this.itemDataCatalogService.getDataByKey("allergens");
        if(allergensList?.length) {
            for(let allergen of allergensList) {
                const found = item.allergens?.find(itemAllergen => itemAllergen.id === allergen.id);
                if (found) {
                    allergen.checked = true;
                } else {
                    allergen.checked = false;
                }
            }
            allergensList = [...allergensList].sort((a, b) => a.name.localeCompare(b.name));
        }
        return allergensList;
    }
}